import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, PlaneTakeoff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const boxRef = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications", { params: { limit: 15 } }).then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [user, load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const markAllRead = async () => {
    await api.post("/notifications/mark-read", {}).catch(() => {});
    setData((d) => ({ items: d.items.map((n) => ({ ...n, read: true })), unread: 0 }));
  };

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      api.post("/notifications/mark-read", { ids: [n.id] }).catch(() => {});
      setData((d) => ({ items: d.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)), unread: Math.max(0, d.unread - 1) }));
    }
    nav(n.link || "/travel-hub");
  };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notification-bell" aria-label="Notifications"
        className="relative p-2 border border-zinc-300 hover:border-primary text-zinc-700 hover:text-primary transition-colors">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-xs font-700 flex items-center justify-center rounded-full">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 max-w-[85vw] bg-white border border-zinc-300 shadow-xl z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-200">
            <span className="text-sm font-700">Notifications</span>
            {data.unread > 0 && (
              <button onClick={markAllRead} data-testid="notifications-mark-all-read"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="px-3 py-6 text-sm text-muted-foreground text-center" data-testid="notifications-empty">
                No notifications yet. Save a flight in My Travel Hub and turn on delay alerts.
              </div>
            ) : data.items.map((n) => (
              <button key={n.id} onClick={() => openNotification(n)} data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${n.read ? "" : "bg-sky-50"}`}>
                <div className="flex items-start gap-2">
                  <PlaneTakeoff className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className={`text-sm truncate ${n.read ? "font-500" : "font-700"}`}>{n.title}</div>
                    <div className="text-xs text-zinc-600 line-clamp-2">{n.body}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="ml-auto mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
